import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { addressesService } from '../services/addressesService';
import { carryItemsService } from '../services/carryItemsService';
import { documentsService } from '../services/documentsService';
import { useSettings } from '../services/SettingsContext';
import { taskServerId } from '../utils/taskServerId';
import { normalizeDueDateForSubmit } from '../utils/normalizeDueDateForSubmit';
import { roundMoney2 } from '../utils/money';
import { formatWaitingDate, historyEventDetails, historyEventLabel, waitingReasonLabel } from '../utils/taskWaiting';
import {
  readTaskDescriptionFromForm,
  readTaskTitleFromForm,
} from '../utils/taskPayload';
import {
  buildTaskDueDate,
  taskDateKey,
  taskTimeValue,
} from '../utils/taskSchedule';
import {
  DEFAULT_WORK_TASK_COLOR,
  PERSONAL_TASK_COLOR,
  isPersonalTask,
  normalizeTaskKindPayload,
} from '../utils/taskKind';
import './TaskForm.css';
import { QRCodeSVG } from 'qrcode.react';

// Категории задач с иконками
export const TASK_CATEGORIES = [
  { value: 'skud', label: 'СКУД', icon: '🔐' },
  { value: 'video', label: 'Видеонаблюдение', icon: '📹' },
  { value: 'scs', label: 'СКС', icon: '🔌' },
  { value: 'alarm', label: 'Сигнализация', icon: '🚨' },
  { value: 'intercom', label: 'Домофония', icon: '📞' },
  { value: 'diagnostic', label: 'Выезд на диагностику/осмотр', icon: '🔧' },
  { value: 'personal', label: 'Личные дела', icon: '👤' },
  { value: 'meeting', label: 'Встреча', icon: '🤝' },
];

// Функция для получения категории по значению
export const getCategoryInfo = (categoryValue) => {
  if (!categoryValue) return null;
  return TASK_CATEGORIES.find(cat => cat.value === categoryValue) || { value: categoryValue, label: categoryValue, icon: '📋' };
};

// Функция для получения иконки категории
export const getCategoryIcon = (categoryValue) => {
  const category = getCategoryInfo(categoryValue);
  return category ? category.icon : '📋';
};

// Функция для получения названия категории
export const getCategoryLabel = (categoryValue) => {
  const category = getCategoryInfo(categoryValue);
  return category ? category.label : categoryValue;
};

// Цвета московского метро + МЦД + дополнительные цвета для личных нужд
const ALL_TASK_COLORS = [
  // ========== МОСКОВСКОЕ МЕТРО ==========
  { value: '#D6083B', label: 'Сокольническая', name: 'metro-red', metro: 'Сокольническая линия' },
  { value: '#4CAF50', label: 'Замоскворецкая', name: 'metro-green', metro: 'Замоскворецкая линия' },
  { value: '#0052CC', label: 'Арбатско-Покровская', name: 'metro-blue', metro: 'Арбатско-Покровская линия' },
  { value: '#00B8D4', label: 'Филёвская', name: 'metro-light-blue', metro: 'Филёвская линия' },
  { value: '#795548', label: 'Кольцевая', name: 'metro-brown', metro: 'Кольцевая линия' },
  { value: '#FF9800', label: 'Калужско-Рижская', name: 'metro-orange', metro: 'Калужско-Рижская линия' },
  { value: '#9C27B0', label: 'Таганско-Краснопресненская', name: 'metro-purple', metro: 'Таганско-Краснопресненская линия' },
  { value: '#FFEB3B', label: 'Калининско-Солнцевская', name: 'metro-yellow', metro: 'Калининско-Солнцевская линия' },
  { value: '#9E9E9E', label: 'Серпуховско-Тимирязевская', name: 'metro-gray', metro: 'Серпуховско-Тимирязевская линия' },
  { value: '#8BC34A', label: 'Люблинско-Дмитровская', name: 'metro-lime', metro: 'Люблинско-Дмитровская линия' },
  { value: '#00BCD4', label: 'Каховская', name: 'metro-cyan', metro: 'Каховская линия' },
  { value: '#607D8B', label: 'Бутовская', name: 'metro-blue-gray', metro: 'Бутовская линия' },
  { value: '#E91E63', label: 'Некрасовская', name: 'metro-pink', metro: 'Некрасовская линия' },
  { value: '#6FB048', label: 'Большая кольцевая', name: 'metro-big-ring', metro: 'Большая кольцевая линия' },

  // ========== МЦД (Московские центральные диаметры) ==========
  { value: '#FF6B35', label: 'МЦК', name: 'mck-orange', metro: 'Московское центральное кольцо' },
  { value: '#1976D2', label: 'МЦД-1', name: 'mcd-1-blue', metro: 'Белорусско-Савёловский диаметр' },
  { value: '#66BB6A', label: 'МЦД-2', name: 'mcd-2-green', metro: 'Курско-Рижский диаметр' },
  { value: '#7B1FA2', label: 'МЦД-3', name: 'mcd-3-purple', metro: 'Ленинградско-Казанский диаметр' },
  { value: '#F57C00', label: 'МЦД-4', name: 'mcd-4-orange', metro: 'Калининско-Солнцевский диаметр' },

  // ========== ЛИЧНЫЕ ЦВЕТА (не совпадают с метро) ==========
  { value: '#F44336', label: 'Красный', name: 'personal-red', metro: null },
  { value: '#EC407A', label: 'Розовый', name: 'personal-pink', metro: null },
  { value: '#BA68C8', label: 'Фиолетовый', name: 'personal-purple', metro: null },
  { value: '#673AB7', label: 'Глубокий фиолетовый', name: 'personal-deep-purple', metro: null },
  { value: '#3F51B5', label: 'Индиго', name: 'personal-indigo', metro: null },
  { value: '#2196F3', label: 'Синий', name: 'personal-blue', metro: null },
  { value: '#03A9F4', label: 'Голубой', name: 'personal-light-blue', metro: null },
  { value: '#26A69A', label: 'Бирюзовый', name: 'personal-cyan', metro: null },
  { value: '#009688', label: 'Зелено-бирюзовый', name: 'personal-teal', metro: null },
  { value: '#689F38', label: 'Зеленый', name: 'personal-green', metro: null },
  { value: '#AED581', label: 'Светло-зеленый', name: 'personal-lime', metro: null },
  { value: '#CDDC39', label: 'Лайм', name: 'personal-lime-green', metro: null },
  { value: '#FDD835', label: 'Желтый', name: 'personal-yellow', metro: null },
  { value: '#FFC107', label: 'Янтарный', name: 'personal-amber', metro: null },
  { value: '#FF6F00', label: 'Оранжевый', name: 'personal-orange', metro: null },
  { value: '#FF5722', label: 'Красно-оранжевый', name: 'personal-deep-orange', metro: null },
  { value: '#8D6E63', label: 'Коричневый', name: 'personal-brown', metro: null },
  { value: '#78909C', label: 'Серый', name: 'personal-gray', metro: null },
  { value: '#546E7A', label: 'Сине-серый', name: 'personal-blue-gray', metro: null },
  { value: '#424242', label: 'Темно-серый', name: 'personal-dark-gray', metro: null },
  { value: '#212121', label: 'Черный', name: 'personal-black', metro: null },
  { value: '#FAFAFA', label: 'Белый', name: 'personal-white', metro: null },
  { value: '#FFD700', label: 'Золотой', name: 'personal-gold', metro: null },
  { value: '#C0C0C0', label: 'Серебряный', name: 'personal-silver', metro: null },
  { value: '#FF1744', label: 'Алый', name: 'personal-scarlet', metro: null },
  { value: '#E040FB', label: 'Фуксия', name: 'personal-fuchsia', metro: null },
  { value: '#536DFE', label: 'Персидский синий', name: 'personal-persian-blue', metro: null },
  { value: '#00E676', label: 'Мятный', name: 'personal-mint', metro: null },
  { value: '#FF6D00', label: 'Ярко-оранжевый', name: 'personal-bright-orange', metro: null },
  { value: '#AA00FF', label: 'Пурпурный', name: 'personal-magenta', metro: null },
];

export const TASK_COLORS = [
  ...ALL_TASK_COLORS.slice(0, 19),
  { value: '#2B2F36', label: 'Графит · свой', name: 'custom-graphite', metro: null },
  { value: '#2A9D8F', label: 'Бирюза · свой', name: 'custom-teal', metro: null },
  { value: '#6C63FF', label: 'Индиго · свой', name: 'custom-indigo', metro: null },
];

// Категории расходов
const EXPENSE_CATEGORIES = [
  { value: 'профи.ру', label: 'профи.ру' },
  { value: 'транспорт', label: 'транспорт' },
  { value: 'налог', label: 'налог' },
];

const DAY_NAMES_SHORT = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];
const MONTH_NAMES_FULL = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];

const entitySearchText = (...values) => values
  .filter(value => value !== null && value !== undefined)
  .join(' ')
  .toLocaleLowerCase('ru-RU')
  .replace(/ё/g, 'е');

export const filterClientsForTask = (clients = [], query = '') => {
  const needle = entitySearchText(query).trim();
  return [...clients]
    .filter(client => !needle || entitySearchText(
      client.name,
      client.company,
      client.phone,
      client.email
    ).includes(needle))
    .sort((left, right) => String(left.name || '').localeCompare(String(right.name || ''), 'ru'));
};

export const filterObjectsForTask = (objects = [], query = '', clientId = '', showAll = false) => {
  const needle = entitySearchText(query).trim();
  return [...objects]
    .filter(object => showAll || !clientId || String(object.client_id || '') === String(clientId))
    .filter(object => !needle || entitySearchText(
      object.name,
      object.address,
      object.metro,
      object.client_name
    ).includes(needle))
    .sort((left, right) => String(left.name || '').localeCompare(String(right.name || ''), 'ru'));
};

const formatDateGnom = (dateStr) => {
  const dateKey = taskDateKey(dateStr);
  if (!dateKey) return null;
  const [yearNumber, monthNumber, dayNumber] = dateKey.split('-').map(Number);
  const d = new Date(yearNumber, monthNumber - 1, dayNumber, 12, 0, 0);
  const dayName = DAY_NAMES_SHORT[d.getDay()];
  const day = d.getDate();
  const month = MONTH_NAMES_FULL[d.getMonth()];
  const year = d.getFullYear();
  return `${dayName}, ${day} ${month}, ${year}`;
};

const TaskForm = ({
  formData,
  setFormData,
  clients = [],
  objects = [],
  onSubmit,
  onCancel,
  onComplete,
  onReopen,
  onWait,
  onResumeWaiting,
  onAddHistory,
  onDelete,
  onMarkPaid,
  onCreatePayment,
  paymentCreatingTaskId = null,
  editingTask = null
}) => {
  const { settings } = useSettings();
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showFullColorPicker, setShowFullColorPicker] = useState(false);
  const [carryTemplates, setCarryTemplates] = useState([]);
  const [carryInput, setCarryInput] = useState('');
  const [historyNote, setHistoryNote] = useState('');
  const [historySaving, setHistorySaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPaymentCreate, setShowPaymentCreate] = useState(false);
  const [relationPicker, setRelationPicker] = useState(null);
  const [relationSearch, setRelationSearch] = useState('');
  const [showAllObjects, setShowAllObjects] = useState(false);
  const [paymentDraft, setPaymentDraft] = useState({
    title: '',
    workAmount: '',
    equipmentAmount: '',
  });
  const addressInputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const modalRef = useRef(null);
  const dateInputRef = useRef(null);
  const titleInputRef = useRef(null);
  const historyNoteInputRef = useRef(null);
  const relationSearchInputRef = useRef(null);
  const previousWorkFieldsRef = useRef(null);
  const [paymentDate, setPaymentDate] = useState(() => {
    const d = new Date();
    const y = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${mo}-${day}`;
  });
  const paymentDraftTotal = roundMoney2(
    Number(paymentDraft.workAmount || 0)
    + Number(paymentDraft.equipmentAmount || 0)
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target) &&
        addressInputRef.current &&
        !addressInputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }

    };

    // Закрытие формы по Escape и по системной кнопке Android «Назад».
    const handleEscape = (event) => {
      if (event.key !== 'Escape') return;

      if (showPaymentCreate) {
        setShowPaymentCreate(false);
      } else if (onCancel) {
        onCancel();
      }

    };
    const handleNativeBack = () => {
      if (document.querySelector('.waiting-dialog-backdrop')) return;
      if (showPaymentCreate) {
        setShowPaymentCreate(false);
      } else if (onCancel) {
        onCancel();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    window.addEventListener('crm:native-back', handleNativeBack);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('crm:native-back', handleNativeBack);
    };
  }, [onCancel, showPaymentCreate]);

  useEffect(() => {
    if (!relationPicker) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const focusTimer = window.setTimeout(() => relationSearchInputRef.current?.focus(), 80);
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setRelationPicker(null);
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener('keydown', closeOnEscape);
      document.body.style.overflow = previousOverflow;
    };
  }, [relationPicker]);

  useEffect(() => {
    carryItemsService.getAll()
      .then(response => setCarryTemplates(response.data || []))
      .catch(error => console.error('Error loading carry templates:', error));
  }, []);

  const loadAddressSuggestions = async (query = '') => {
    try {
      const response = await addressesService.getSuggestions(query);
      const suggestions = (response.data || []).map(item => typeof item === 'string' ? item : item.address).filter(Boolean);
      setAddressSuggestions(suggestions);
      setShowSuggestions(suggestions.length > 0);
    } catch (error) {
      console.error('Error fetching address suggestions:', error);
      setAddressSuggestions([]);
    }
  };

  const handleAddressChange = async (e) => {
    const value = e.target.value;
    setFormData({ ...formData, address: value });

    if (value.length >= 2) {
      await loadAddressSuggestions(value);

    } else {
      setAddressSuggestions([]);
      setShowSuggestions(false);
    }

  };

  const addCarryItem = (value) => {
    const label = String(value || '').trim().replace(/\s+/g, ' ');
    if (!label) return;
    const current = Array.isArray(formData.carry_items) ? formData.carry_items : [];
    if (!current.some(item => item.toLowerCase() === label.toLowerCase())) {
      setFormData({ ...formData, carry_items: [...current, label] });
    }
    setCarryInput('');
  };

  const removeCarryItem = (label) => {
    setFormData({ ...formData, carry_items: (formData.carry_items || []).filter(item => item !== label) });
  };

  const handleAddressSelect = (address) => {
    // Ensure we get a string, not an object
    const addressString = typeof address === 'string' ? address : address.address || address;
    setFormData({ ...formData, address: addressString });
    setShowSuggestions(false);
    setAddressSuggestions([]);
  };

  const addExpense = () => {
    const next = Array.isArray(formData.expenses) ? [...formData.expenses] : [];
    next.push({ amount: '', category: '', description: '' });
    setFormData({ ...formData, expenses: next });
  };

  const updateExpense = (index, field, value) => {
    const next = Array.isArray(formData.expenses) ? [...formData.expenses] : [];
    if (!next[index]) return;
    next[index] = { ...next[index], [field]: field === 'amount' ? value : value };
    setFormData({ ...formData, expenses: next });
  };

  const removeExpense = (index) => {
    const next = Array.isArray(formData.expenses) ? [...formData.expenses] : [];
    next.splice(index, 1);
    setFormData({ ...formData, expenses: next });
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    let data = { ...formData };
    // Android IME может ещё не успеть обновить React state в момент нажатия
    // «Сохранить». Берём фактическое значение из DOM-поля формы.
    data.title = readTaskTitleFromForm(e.currentTarget, formData.title);
    data.description = readTaskDescriptionFromForm(
      e.currentTarget,
      formData.description
    );
    if (!data.title) {
      titleInputRef.current?.setCustomValidity('Введите название задачи');
      titleInputRef.current?.reportValidity();
      titleInputRef.current?.focus();
      return;
    }
    titleInputRef.current?.setCustomValidity('');
    // Ключ due_date обязателен: иначе JSON.stringify выбросит поле и PUT не обновит дату в БД
    if (!Object.prototype.hasOwnProperty.call(data, 'due_date')) {
      data.due_date = '';
    }
    data.has_time = Boolean(data.due_date && data.has_time);
    data.reminder_day_before = data.reminder_day_before !== false;
    data.reminder_morning = data.reminder_morning !== false;
    data.reminder_two_hours = data.reminder_two_hours !== false;
    if (data.due_date) {
      data.due_date = buildTaskDueDate(
        taskDateKey(data.due_date),
        data.has_time,
        taskTimeValue(data.due_date)
      );
    }
    normalizeDueDateForSubmit(data);

    // Обработка повторяющихся задач
    if (!data.is_recurring) {
      data.is_recurring = false;
      data.recurrence_type = null;
    } else {
      // Если задача повторяющаяся, но тип не выбран, устанавливаем 'weekly'
      if (!data.recurrence_type) {
        data.recurrence_type = 'weekly';
      }
    }
    // Подготовим расходы: оставим только валидные (с категорией и положительной суммой)
    if (Array.isArray(data.expenses)) {
      data.expenses = data.expenses
        .map(e => ({
          amount: roundMoney2(e.amount),
          category: (e.category || '').toString().trim().toLowerCase(),
          description: (e.description || '').toString().trim() || undefined,
        }))
        .filter(e => Number.isFinite(e.amount) && e.amount > 0 && e.category);
    }
    if (data.cost === '' || data.cost == null) data.cost = 0;
    else data.cost = roundMoney2(data.cost);
    data = normalizeTaskKindPayload(data);

    const pendingHistoryNote = editingTask && onAddHistory
      ? String(historyNoteInputRef.current?.value ?? historyNote).trim()
      : '';

    setIsSubmitting(true);
    try {
      if (pendingHistoryNote) {
        await onAddHistory(pendingHistoryNote);
        setHistoryNote('');
      }
      await onSubmit(data);
    } finally {
      setIsSubmitting(false);
    }
  };

  const saveHistoryNote = async () => {
    const note = String(historyNoteInputRef.current?.value ?? historyNote).trim();
    if (!note || !onAddHistory) return;
    setHistorySaving(true);
    try {
      await onAddHistory(note);
      setHistoryNote('');
    } finally {
      setHistorySaving(false);
    }
  };

  const openPaymentCreate = () => {
    setPaymentDraft({
      title: String(editingTask?.title || '').trim(),
      workAmount: Number(editingTask?.cost || 0) || '',
      equipmentAmount: '',
    });
    setShowPaymentCreate(true);
  };

  const submitPaymentCreate = async (event) => {
    event.preventDefault();

    if (!onCreatePayment || !editingTask) return;

    const workAmount = roundMoney2(
      Number(paymentDraft.workAmount || 0)
    );
    const equipmentAmount = roundMoney2(
      Number(paymentDraft.equipmentAmount || 0)
    );
    const title = String(paymentDraft.title || '').trim();

    if (!title || workAmount < 0 || equipmentAmount < 0) {
      return;
    }

    if (roundMoney2(workAmount + equipmentAmount) <= 0) {
      return;
    }

    const created = await onCreatePayment(editingTask, {
      title,
      workAmount,
      equipmentAmount,
    });

    if (created !== false) {
      setShowPaymentCreate(false);
    }
  };

  const handleModalClick = (e) => {
    // currentTarget - это элемент, на котором установлен обработчик (модальное окно)
    // target - это элемент, на который был сделан клик
    // Если они равны, значит клик был именно на backdrop (пустое место)
    if (e.target === e.currentTarget) {
      // Клик был на backdrop - закрываем форму
      if (onCancel) {
        onCancel();
      }
    }
    // Если target !== currentTarget, значит клик был на дочернем элементе (контенте формы)
    // В этом случае ничего не делаем - форма остается открытой
  };

  const dateFormatted = formatDateGnom(formData.due_date);
  const dueDateOnly = taskDateKey(formData.due_date);
  const dueTime = taskTimeValue(formData.due_date);
  const hasTime = formData.has_time === true;
  const firstExpenseDisplay = (formData.expenses && formData.expenses[0] != null)
    ? ((formData.expenses[0].amount !== undefined && formData.expenses[0].amount !== null && formData.expenses[0].amount !== '' && Number(formData.expenses[0].amount) !== 0) ? formData.expenses[0].amount : '')
    : '';
  const setFirstExpenseAmount = (val) => {
    const next = Array.isArray(formData.expenses) ? [...formData.expenses] : [];
    const parsed = val === '' ? NaN : parseFloat(String(val).replace(',', '.'));
    const num = val === '' ? '' : (Number.isFinite(parsed) ? roundMoney2(parsed) : 0);
    if (next.length === 0) {
      next.push({ amount: num, category: 'транспорт', description: '' });
    } else {
      next[0] = { ...next[0], amount: num };
    }
    setFormData({ ...formData, expenses: next });
  };
  const displayStatus = (editingTask && (editingTask.status === 'pending' || editingTask.status === 'cancelled')) ? 'in_progress' : (editingTask?.status || 'in_progress');
  const isPersonal = isPersonalTask(formData);
  const selectedClient = clients.find(client => String(client.id) === String(formData.client_id || '')) || null;
  const selectedObject = objects.find(object => String(object.id) === String(formData.object_id || '')) || null;
  const clientObjectCount = selectedClient
    ? objects.filter(object => String(object.client_id || '') === String(selectedClient.id)).length
    : 0;
  const visibleClients = useMemo(
    () => filterClientsForTask(clients, relationSearch),
    [clients, relationSearch]
  );
  const visibleObjects = useMemo(
    () => filterObjectsForTask(objects, relationSearch, formData.client_id, showAllObjects),
    [objects, relationSearch, formData.client_id, showAllObjects]
  );
  const firstColors = TASK_COLORS.slice(0, 7);
  const selectedColorLabel = TASK_COLORS.find(color => color.value === formData.color)?.label || 'Свой цвет';
  const setWorkTask = (workTask) => {
    if (!workTask) {
      previousWorkFieldsRef.current = {
        client_id: formData.client_id || '',
        object_id: formData.object_id || '',
        cost: formData.cost || 0,
        category: formData.category || '',
        color: formData.color || DEFAULT_WORK_TASK_COLOR,
        carry_items: Array.isArray(formData.carry_items) ? formData.carry_items : [],
        expenses: Array.isArray(formData.expenses) ? formData.expenses : [],
      };
      setFormData({
        ...formData,
        is_personal: true,
        client_id: '',
        object_id: '',
        cost: 0,
        category: 'personal',
        color: PERSONAL_TASK_COLOR,
        carry_items: [],
        expenses: [],
      });
      return;
    }

    const restored = previousWorkFieldsRef.current || {};
    setFormData({
      ...formData,
      ...restored,
      is_personal: false,
      category: restored.category ?? (formData.category === 'personal' ? '' : formData.category),
      color: restored.color || DEFAULT_WORK_TASK_COLOR,
    });
    previousWorkFieldsRef.current = null;
  };

  const openRelationPicker = (kind) => {
    setRelationSearch('');
    setShowAllObjects(false);
    setRelationPicker(kind);
  };

  const chooseClient = (client) => {
    const clientId = client?.id || null;
    setFormData(current => {
      const linkedObject = objects.find(object => String(object.id) === String(current.object_id || ''));
      const objectMatchesClient = linkedObject
        && (!linkedObject.client_id || String(linkedObject.client_id) === String(clientId || ''));
      return {
        ...current,
        client_id: clientId,
        object_id: objectMatchesClient ? current.object_id : '',
        address: !objectMatchesClient && linkedObject?.address === current.address ? '' : current.address,
      };
    });
    setRelationPicker(null);
  };

  const chooseObject = (object) => {
    setFormData(current => ({
      ...current,
      object_id: object?.id || '',
      client_id: object?.client_id || current.client_id || null,
      address: object?.address || current.address || '',
    }));
    setRelationPicker(null);
  };

  return createPortal(
    <div className={`task-form-modal gnom-form ${isPersonal ? 'gnom-personal-mode' : ''}`} ref={modalRef} onClick={handleModalClick}>
      <div className="task-form-content gnom-content" onClick={(e) => e.stopPropagation()}>
        <header className="gnom-form-header">
          <button type="button" className="gnom-form-back" onClick={onCancel} aria-label="Назад">
            <span aria-hidden="true">←</span>
            <span>Назад</span>
          </button>
          <h2 className="gnom-form-title">{editingTask ? 'Редактировать действие' : 'Новое действие'}</h2>
          <button
            type="button"
            form="gnom-task-form"
            className="gnom-form-header-submit"
            disabled={isSubmitting}
            onClick={() => {
              const form = modalRef.current?.querySelector('#gnom-task-form');
              if (form) form.requestSubmit();
            }}
            aria-label={editingTask ? 'Сохранить изменения задачи' : 'Создать задачу'}
          >
            {isSubmitting ? '…' : editingTask ? 'Сохранить' : 'Создать'}
          </button>
        </header>

        <form id="gnom-task-form" onSubmit={handleSubmit} className="gnom-form-shell">
          <div className="gnom-form-body">
          {/* Заголовок события */}
          <div className="gnom-form-row">
            <span className="gnom-form-row-icon">≡</span>
            <div className="gnom-form-row-content">
              <input
                ref={titleInputRef}
                name="title"
                type="text"
                value={formData.title || ''}
                onChange={(e) => {
                  e.target.setCustomValidity('');
                  setFormData({ ...formData, title: e.target.value });
                }}
                required
                autoComplete="on"
                autoCapitalize="sentences"
                spellCheck="true"
                enterKeyHint="done"
                placeholder="Заголовок события"
              />
            </div>
            <span className="gnom-form-row-right" title="Шаблоны">📄</span>
          </div>

          <label className={`gnom-task-kind ${isPersonal ? 'personal' : 'work'}`}>
            <input
              type="checkbox"
              checked={!isPersonal}
              onChange={(event) => setWorkTask(event.target.checked)}
            />
            <span className="gnom-task-kind-switch" aria-hidden="true" />
            <span className="gnom-task-kind-copy">
              <strong>{isPersonal ? 'Личная задача' : 'Рабочая задача'}</strong>
              <small>{isPersonal ? 'Без клиентов, объектов и денег — только твоё дело' : 'Клиенты, объекты, расходы и оплата'}</small>
            </span>
            <span className="gnom-task-kind-icon" aria-hidden="true">{isPersonal ? '👤' : '🧰'}</span>
          </label>

          {/* Статус: Завершено | Отменено — только при редактировании */}
          {editingTask && (
            <>
              <div className="gnom-form-row">
                <span className="gnom-form-row-icon">✓</span>
                <div className="gnom-form-row-content">
                  <div className="gnom-status-row">
                    <label className={`gnom-status-option ${displayStatus === 'in_progress' ? 'active' : ''}`}>
                      <input
                        type="radio"
                        name="gnom-status"
                        checked={displayStatus === 'in_progress'}
                        onChange={() => {
                          if (editingTask.status === 'waiting' && onResumeWaiting) onResumeWaiting();
                          else if (editingTask.status === 'completed' && onReopen && window.confirm('Вернуть задачу в работу?')) onReopen(taskServerId(editingTask));
                        }}
                      />
                      В работе
                    </label>
                    <label className={`gnom-status-option ${displayStatus === 'completed' ? 'active' : ''}`}>
                      <input
                        type="radio"
                        name="gnom-status"
                        checked={displayStatus === 'completed'}
                        onChange={() => onComplete && window.confirm('Завершить задачу?') && onComplete(taskServerId(editingTask))}
                      />
                      Завершено
                    </label>
                    <label className={`gnom-status-option waiting ${displayStatus === 'waiting' ? 'active' : ''}`}>
                      <input
                        type="radio"
                        name="gnom-status"
                        checked={displayStatus === 'waiting'}
                        onChange={() => onWait && onWait(editingTask)}
                      />
                      В ожидании
                    </label>
                    <label className={`gnom-status-option ${displayStatus === 'cancelled' ? 'active' : ''}`}>
                      <input
                        type="radio"
                        name="gnom-status"
                        checked={displayStatus === 'cancelled'}
                        onChange={() => onDelete && window.confirm('Отменить задачу?') && onDelete(taskServerId(editingTask))}
                      />
                      Отменено
                    </label>
                  </div>
                </div>
              </div>
              {editingTask.status === 'waiting' && (
                <div className="gnom-form-row gnom-waiting-summary">
                  <span className="gnom-form-row-icon">⏸</span>
                  <div className="gnom-form-row-content">
                    <strong>{waitingReasonLabel(editingTask.waiting_reason)}</strong>
                    {editingTask.waiting_comment && <p>{editingTask.waiting_comment}</p>}
                    {editingTask.waiting_until && <small>Напомнить: {formatWaitingDate(editingTask.waiting_until)}</small>}
                    <div className="gnom-waiting-summary-actions">
                      {onWait && <button type="button" onClick={() => onWait(editingTask)}>Изменить ожидание</button>}
                      {onResumeWaiting && <button type="button" onClick={onResumeWaiting}>▶ Вернуть в работу</button>}
                    </div>
                  </div>
                </div>
              )}
              {editingTask.status === 'completed' && !isPersonal && (
                <div className="gnom-form-row">
                  <span className="gnom-form-row-icon">💰</span>
                  <div className="gnom-form-row-content">
                    <div className="gnom-status-row gnom-status-row-paid">
                      <span
                        className={`gnom-status-badge ${editingTask.is_paid ? 'paid' : 'unpaid'}`}
                      >
                        {editingTask.is_paid ? 'Оплачено' : 'Не оплачено'}
                      </span>
                      {!editingTask.is_paid && onMarkPaid && (editingTask.cost || 0) > 0 && (
                        <>
                          <input
                            type="date"
                            className="gnom-status-pay-date"
                            value={paymentDate}
                            onChange={(e) => setPaymentDate(e.target.value || '')}
                            title="Дата оплаты"
                          />
                          <button
                            type="button"
                            className="gnom-status-pay-button"
                            onClick={() => {
                              if (window.confirm('Отметить задачу как оплаченную?')) {
                                onMarkPaid(taskServerId(editingTask), paymentDate || null);
                              }
                            }}
                          >
                            💳 Оплачено
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {editingTask.status === 'completed' && !isPersonal && (
                <div className="gnom-form-row">
                  <span className="gnom-form-row-icon">📄</span>
                  <div className="gnom-form-row-content">
                    <div className="gnom-documents-title">Документы по заявке</div>
                    <div className="gnom-documents-list">
                      {(editingTask.documents || []).map(document => (
                        <button
                          key={document.id}
                          type="button"
                          onClick={() => documentsService.download(document.id, document.file_name)}
                        >
                          Скачать акт №{document.document_number}
                        </button>
                      ))}
                      <button
                        type="button"
                        className="create"
                        onClick={() => window.dispatchEvent(new CustomEvent('crm:create-work-act', { detail: { taskId: taskServerId(editingTask) } }))}
                      >
                        + Создать акт
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Дата и необязательное время */}
          <div className="gnom-form-row gnom-datetime-row">
            <span className="gnom-form-row-icon">📅</span>
            <div className="gnom-form-row-content gnom-datetime-content">
              <input
                ref={dateInputRef}
                id="task-due-date-input"
                type="date"
                value={dueDateOnly}
                onChange={(e) => {
                  const dateVal = (e.target.value || '').trim();
                  setFormData({
                    ...formData,
                    due_date: buildTaskDueDate(dateVal, hasTime, dueTime),
                    has_time: dateVal ? hasTime : false,
                  });
                }}
                className="gnom-datetime-input-hidden"
                aria-label="Дата"
              />
              <div
                className="gnom-datetime-primary"
                onClick={() => { const el = dateInputRef.current; if (el) { el.showPicker?.(); el.click(); } }}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); dateInputRef.current?.click(); } }}
                role="button"
                tabIndex={0}
                aria-label="Выбрать дату"
              >
                {dateFormatted || 'Выберите дату'}
              </div>
            </div>
            <span className="gnom-form-row-right" title="Повтор">🔄</span>
            <span className="gnom-form-row-right" title="Сбросить">↻</span>
          </div>

          {dueDateOnly && (
            <div className="gnom-form-row gnom-time-settings-row">
              <span className="gnom-form-row-icon">🕒</span>
              <div className="gnom-form-row-content gnom-time-settings">
                <label className="gnom-check-line">
                  <input
                    type="checkbox"
                    checked={!hasTime}
                    onChange={(e) => {
                      const nextHasTime = !e.target.checked;
                      setFormData({
                        ...formData,
                        has_time: nextHasTime,
                        due_date: buildTaskDueDate(
                          dueDateOnly,
                          nextHasTime,
                          dueTime === '00:00' ? '09:00' : dueTime
                        ),
                      });
                    }}
                  />
                  <span>Время не требуется</span>
                </label>
                {hasTime && (
                  <input
                    type="time"
                    value={dueTime === '00:00' ? '09:00' : dueTime}
                    onChange={(e) => setFormData({
                      ...formData,
                      has_time: true,
                      due_date: buildTaskDueDate(dueDateOnly, true, e.target.value || '09:00'),
                    })}
                    aria-label="Время задачи"
                    className="gnom-time-input"
                  />
                )}
              </div>
            </div>
          )}

          {dueDateOnly && (
            <div className="gnom-form-row gnom-reminders-row">
              <span className="gnom-form-row-icon">🔔</span>
              <div className="gnom-form-row-content">
                <div className="gnom-reminders-title">Уведомления</div>
                <label className="gnom-check-line">
                  <input
                    type="checkbox"
                    checked={formData.reminder_day_before !== false}
                    onChange={(e) => setFormData({ ...formData, reminder_day_before: e.target.checked })}
                  />
                  <span>{hasTime ? 'За сутки до времени задачи' : 'Накануне в 10:00'}</span>
                </label>
                <label className="gnom-check-line">
                  <input
                    type="checkbox"
                    checked={formData.reminder_morning !== false}
                    onChange={(e) => setFormData({ ...formData, reminder_morning: e.target.checked })}
                  />
                  <span>В 10:00 в день задачи</span>
                </label>
                {hasTime && (
                  <label className="gnom-check-line">
                    <input
                      type="checkbox"
                      checked={formData.reminder_two_hours !== false}
                      onChange={(e) => setFormData({ ...formData, reminder_two_hours: e.target.checked })}
                    />
                    <span>За 2 часа до времени задачи</span>
                  </label>
                )}
              </div>
            </div>
          )}

          {!isPersonal && (
            <section className="gnom-relation-card" aria-label="Клиент и объект">
              <div className="gnom-relation-heading">
                <div>
                  <span>ПРИВЯЗКА ЗАДАЧИ</span>
                  <strong>Клиент и объект</strong>
                </div>
                {(selectedClient || selectedObject) && <em>Связано</em>}
              </div>

              {settings.show_client && (
                <button type="button" className="gnom-relation-field" onClick={() => openRelationPicker('client')}>
                  <span className="gnom-relation-icon client" aria-hidden="true">👤</span>
                  <span className="gnom-relation-copy">
                    <small>Клиент</small>
                    <strong>{selectedClient?.name || 'Выбрать клиента'}</strong>
                    <span>{selectedClient
                      ? [selectedClient.company, selectedClient.phone, `${clientObjectCount} объект${clientObjectCount === 1 ? '' : clientObjectCount >= 2 && clientObjectCount <= 4 ? 'а' : 'ов'}`].filter(Boolean).join(' · ')
                      : 'Поиск по имени, компании или телефону'}</span>
                  </span>
                  <span className="gnom-relation-chevron" aria-hidden="true">›</span>
                </button>
              )}

              <button type="button" className="gnom-relation-field" onClick={() => openRelationPicker('object')}>
                <span className="gnom-relation-icon object" aria-hidden="true">🏢</span>
                <span className="gnom-relation-copy">
                  <small>Объект</small>
                  <strong>{selectedObject?.name || 'Выбрать объект'}</strong>
                  <span>{selectedObject
                    ? (selectedObject.address || selectedObject.client_name || 'Адрес не указан')
                    : selectedClient ? 'Покажем объекты выбранного клиента' : 'Поиск по названию или адресу'}</span>
                </span>
                <span className="gnom-relation-chevron" aria-hidden="true">›</span>
              </button>
            </section>
          )}

          {/* Расход / Приход */}
          {!isPersonal && <div className="gnom-form-row">
            <span className="gnom-form-row-icon">📷</span>
            <div className="gnom-form-row-content">
              <div className="gnom-finance-row">
                <div className="gnom-finance-field">
                  <label>Расход</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={firstExpenseDisplay}
                    onChange={(e) => setFirstExpenseAmount(e.target.value)}
                    placeholder=""
                  />
                </div>
                <div className="gnom-finance-field">
                  <label>Приход</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={(formData.cost !== undefined && formData.cost !== null && formData.cost !== '' && Number(formData.cost) !== 0) ? formData.cost : ''}
                    onChange={(e) => {
                      const raw = e.target.value;
                      if (raw === '') setFormData({ ...formData, cost: '' });
                      else {
                        const parsed = parseFloat(String(raw).replace(',', '.'));
                        setFormData({ ...formData, cost: Number.isFinite(parsed) ? roundMoney2(parsed) : '' });
                      }
                    }}
                    placeholder=""
                  />
                </div>
              </div>
            </div>
          </div>}

          {!isPersonal && settings.show_expenses && (formData.expenses || []).length > 0 && (
            <div className="gnom-form-row">
              <div className="gnom-form-row-content">
                <div className="gnom-expenses-list">
                  {(formData.expenses || []).map((exp, idx) => (
                    <div key={idx} className="gnom-expense-item">
                      <input type="number" min="0" step="0.01" value={(exp.amount !== undefined && exp.amount !== null && exp.amount !== '' && Number(exp.amount) !== 0) ? exp.amount : ''} onChange={(e) => updateExpense(idx, 'amount', e.target.value === '' ? '' : e.target.value)} placeholder="Сумма" />
                      <select value={exp.category || ''} onChange={(e) => updateExpense(idx, 'category', e.target.value)}>
                        <option value="">Категория</option>
                        {EXPENSE_CATEGORIES.map(cat => <option key={cat.value} value={cat.value}>{cat.label}</option>)}
                      </select>
                      <input className="gnom-expense-desc" type="text" value={exp.description || ''} onChange={(e) => updateExpense(idx, 'description', e.target.value)} autoComplete="on" autoCapitalize="sentences" spellCheck="true" placeholder="Описание" />
                      <button type="button" onClick={() => removeExpense(idx)}>✖</button>
                    </div>
                  ))}
                  <button type="button" className="gnom-add-expense" onClick={addExpense}>+ Добавить расход</button>
                </div>
              </div>
            </div>
          )}
          {!isPersonal && settings.show_expenses && (!formData.expenses || formData.expenses.length === 0) && (
            <div className="gnom-form-row">
              <div className="gnom-form-row-content">
                <button type="button" className="gnom-add-expense" onClick={addExpense}>+ Добавить расход</button>
              </div>
            </div>
          )}

          {/* Комментарии */}
          <div className={`gnom-form-row ${isPersonal ? 'gnom-personal-description' : ''}`}>
            <span className="gnom-form-row-icon">{isPersonal ? '✍️' : '≡'}</span>
            <div className="gnom-form-row-content">
              {isPersonal && <div className="gnom-personal-description-title">Описание и заметки</div>}
              <textarea
                name="description"
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                autoComplete="on"
                autoCapitalize="sentences"
                spellCheck="true"
                placeholder={isPersonal ? 'Что нужно сделать, купить, не забыть или обдумать…' : 'Комментарии'}
                rows={isPersonal ? 5 : 2}
              />
            </div>
          </div>

          {/* Адрес */}
          {settings.show_address && (
            <div className="gnom-form-row">
              <span className="gnom-form-row-icon">📍</span>
              <div className="gnom-form-row-content" style={{ position: 'relative' }}>
                <input
                  ref={addressInputRef}
                  type="text"
                  value={formData.address || ''}
                  onChange={handleAddressChange}
                  onFocus={() => loadAddressSuggestions(formData.address || '')}
                  autoComplete="street-address"
                  autoCapitalize="words"
                  spellCheck="true"
                  placeholder={isPersonal ? 'Место, если оно важно' : 'Адрес'}
                />
                {showSuggestions && addressSuggestions.length > 0 && (
                  <div ref={suggestionsRef} className="address-suggestions" style={{ background: '#1e1e22', border: '1px solid #2d2d32', borderRadius: '8px', marginTop: '4px', maxHeight: '160px', overflowY: 'auto' }}>
                    {addressSuggestions.map((address, index) => {
                      const addressString = typeof address === 'string' ? address : (address.address || String(address));
                      return (
                        <div key={index} className="address-suggestion-item" style={{ padding: '10px 12px', color: '#e8e8e8', cursor: 'pointer', borderBottom: '1px solid #2d2d32' }} onClick={() => handleAddressSelect(addressString)}>
                          {addressString}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {!isPersonal && <div className="gnom-form-row">
            <span className="gnom-form-row-icon">🧰</span>
            <div className="gnom-form-row-content">
              <div className="gnom-carry-title">Не забыть с собой</div>
              <div className="gnom-carry-selected">
                {(formData.carry_items || []).map(item => (
                  <button key={item} type="button" className="gnom-carry-chip selected" onClick={() => removeCarryItem(item)} title="Убрать">
                    {item} ×
                  </button>
                ))}
              </div>
              <div className="gnom-carry-add">
                <input
                  value={carryInput}
                  onChange={(e) => setCarryInput(e.target.value)}
                  autoComplete="on"
                  autoCapitalize="sentences"
                  spellCheck="true"
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCarryItem(carryInput); } }}
                  placeholder="Добавить предмет"
                />
                <button type="button" onClick={() => addCarryItem(carryInput)}>+</button>
              </div>
              <div className="gnom-carry-templates">
                {carryTemplates
                  .filter(item => !(formData.carry_items || []).some(selected => selected.toLowerCase() === item.name.toLowerCase()))
                  .slice(0, 10)
                  .map(item => <button key={item.id} type="button" className="gnom-carry-chip" onClick={() => addCarryItem(item.name)}>{item.name}</button>)}
              </div>
            </div>
          </div>}

          {/* Цвет для этого события */}
          {!isPersonal && <div className="gnom-form-row">
            <span className="gnom-form-row-icon">🎨</span>
            <div className="gnom-form-row-content">
              <div style={{ fontSize: '14px', color: '#9a9aa0', marginBottom: '6px' }}>Цвет: {selectedColorLabel}</div>
              <div className="gnom-color-row">
                {firstColors.map((color, idx) => (
                  <button
                    key={color.value}
                    type="button"
                    className={`gnom-color-swatch ${formData.color === color.value ? 'selected' : ''}`}
                    style={{ backgroundColor: color.value }}
                    onClick={() => setFormData({ ...formData, color: color.value })}
                    title={color.label}
                  />
                ))}
                <button type="button" className="gnom-color-more" onClick={() => setShowFullColorPicker(!showFullColorPicker)} title="Ещё цвета" />
              </div>
              {showFullColorPicker && (
                <div className="gnom-color-full">
                  {TASK_COLORS.map(color => (
                    <button
                      key={color.value}
                      type="button"
                      className={`gnom-color-option ${formData.color === color.value ? 'selected' : ''}`}
                      onClick={() => { setFormData({ ...formData, color: color.value }); setShowFullColorPicker(false); }}
                      title={color.label}
                    >
                      <span className="gnom-color-option-dot" style={{ backgroundColor: color.value }} />
                      <span>{color.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>}

          {!isPersonal && editingTask?.payment?.sbp_url && (
            <div className="gnom-payment-panel">
              <div className="gnom-payment-panel-header">
                <div>
                  <strong>Оплата по СБП</strong>
                  <span>
                    {Number(editingTask.payment.amount || 0)
                      .toLocaleString('ru-RU')} ₽
                  </span>
                  <small className="gnom-payment-split">
                    Работа: {Number(
                      editingTask.payment.work_amount
                      ?? editingTask.payment.amount
                      ?? 0
                    ).toLocaleString('ru-RU')} ₽
                    {' · '}
                    Оборудование: {Number(
                      editingTask.payment.equipment_amount || 0
                    ).toLocaleString('ru-RU')} ₽
                  </small>
                </div>

                <span className="gnom-payment-status">
                  {Number(editingTask.payment.status) === 1
                    ? 'Оплачено'
                    : Number(editingTask.payment.status) === 3
                      ? 'Отменено'
                      : Number(editingTask.payment.status) === 4
                        ? 'Ошибка'
                        : 'Ожидает оплаты'}
                </span>
              </div>

              <div className="gnom-payment-panel-content">
                <div className="gnom-payment-qr">
                  <QRCodeSVG
                    value={editingTask.payment.sbp_url}
                    size={220}
                    level="M"
                    includeMargin
                  />
                </div>

                <div className="gnom-payment-details">
                  <div className="gnom-payment-title">
                    {editingTask.payment.title || editingTask.title}
                  </div>

                  <div className="gnom-payment-link">
                    {editingTask.payment.sbp_url}
                  </div>

                  <div className="gnom-payment-buttons">
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(
                            editingTask.payment.sbp_url
                          );
                          window.alert('Ссылка СБП скопирована');
                        } catch (error) {
                          console.error(
                            'Не удалось скопировать ссылку:',
                            error
                          );
                        }
                      }}
                    >
                      📋 Скопировать СБП
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        window.open(
                          editingTask.payment.sbp_url,
                          '_blank',
                          'noopener,noreferrer'
                        );
                      }}
                    >
                      📱 Открыть СБП
                    </button>

                    {editingTask.payment.payment_url && (
                      <button
                        type="button"
                        className="btn-payment-card"
                        onClick={() => {
                          window.open(
                            editingTask.payment.payment_url,
                            '_blank',
                            'noopener,noreferrer'
                          );
                        }}
                      >
                        💳 Оплата картой
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Повторяющаяся задача — компактно */}
          <div className="gnom-form-row gnom-recurrence-row">
            <span className="gnom-form-row-icon">🔄</span>
            <div className="gnom-form-row-content gnom-recurrence-content">
              <label className="gnom-recurrence-label">
                <input
                  type="checkbox"
                  checked={formData.is_recurring || false}
                  onChange={(e) => setFormData({ ...formData, is_recurring: e.target.checked, recurrence_type: e.target.checked ? (formData.recurrence_type || 'weekly') : '' })}
                />
                <span>Повторяющаяся задача</span>
              </label>
              {formData.is_recurring && (
                <select
                  className="gnom-recurrence-select"
                  value={formData.recurrence_type || 'weekly'}
                  onChange={(e) => setFormData({ ...formData, recurrence_type: e.target.value })}
                >
                  <option value="weekly">Каждую неделю</option>
                  <option value="monthly">Каждый месяц</option>
                  <option value="yearly">Каждый год</option>
                </select>
              )}
            </div>
          </div>

          {editingTask && (
            <div className="gnom-form-row gnom-history-row">
              <span className="gnom-form-row-icon">🕘</span>
              <div className="gnom-form-row-content">
                <div className="gnom-history-title">История задачи</div>
                {onAddHistory && (
                  <div className="gnom-history-add">
                    <textarea
                      ref={historyNoteInputRef}
                      value={historyNote}
                      onChange={(event) => setHistoryNote(event.target.value)}
                      autoComplete="on"
                      autoCapitalize="sentences"
                      spellCheck="true"
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' && !event.shiftKey) {
                          event.preventDefault();
                          void saveHistoryNote();
                        }
                      }}
                      rows="2"
                      placeholder="Добавить заметку — необязательно"
                    />
                    <button type="button" disabled={historySaving} onClick={() => void saveHistoryNote()}>
                      {historySaving ? 'Сохраняю…' : 'Сохранить заметку'}
                    </button>
                  </div>
                )}
                <div className="gnom-history-list">
                  {(editingTask.history || []).length === 0 && <span className="gnom-history-empty">История появится после первого изменения</span>}
                  {(editingTask.history || []).map(event => {
                    const details = historyEventDetails(event);
                    return (
                      <div key={event.id || `${event.event_type}-${event.created_at}`} className="gnom-history-item">
                        <div>
                          <strong>{historyEventLabel(event)}</strong>
                          <time>{formatWaitingDate(event.created_at)}</time>
                        </div>
                        {details && <span>{details}</span>}
                        {event.reason && <span>{waitingReasonLabel(event.reason)}</span>}
                        {event.comment && <p>{event.comment}</p>}
                        {event.remind_at && <small>Напоминание: {formatWaitingDate(event.remind_at)}</small>}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
          </div>

          <div className="gnom-form-footer">
            {editingTask && (
              <div className="gnom-form-actions-extra">
              {!isPersonal && onCreatePayment
                && !editingTask.payment
                && !editingTask.is_paid
                && (
                  <button
                    type="button"
                    className="btn-payment"
                    disabled={
                      paymentCreatingTaskId === taskServerId(editingTask)
                    }
                    onClick={openPaymentCreate}
                  >
                    {paymentCreatingTaskId === taskServerId(editingTask)
                      ? 'Создаю оплату…'
                      : 'Создать оплату'}
                  </button>
                )}
              {onComplete && editingTask.status !== 'completed' && (
                <button type="button" onClick={() => window.confirm('Завершить задачу?') && onComplete(taskServerId(editingTask))}>Завершить</button>
              )}
              {onWait && !['completed', 'waiting'].includes(editingTask.status) && (
                <button type="button" className="btn-waiting" onClick={() => onWait(editingTask)}>В ожидание</button>
              )}
              {onResumeWaiting && editingTask.status === 'waiting' && (
                <button type="button" className="btn-waiting" onClick={onResumeWaiting}>В работу</button>
              )}
              {onReopen && editingTask.status === 'completed' && (
                <button type="button" onClick={() => window.confirm('Вернуть в работу?') && onReopen(taskServerId(editingTask))}>В работу</button>
              )}
              {onDelete && (
                <button type="button" className="btn-danger" onClick={() => window.confirm('Удалить задачу?') && onDelete(taskServerId(editingTask))}>Удалить</button>
              )}
              </div>
            )}
            <button
              type="submit"
              className={`gnom-form-submit ${isSubmitting ? 'is-saving' : ''}`}
              title={
                isSubmitting
                  ? 'Сохранение…'
                  : editingTask
                    ? 'Сохранить изменения'
                    : 'Создать задачу'
              }
              aria-label={
                isSubmitting
                  ? 'Сохранение задачи'
                  : editingTask
                    ? 'Сохранить изменения задачи'
                    : 'Создать задачу'
              }
              disabled={isSubmitting}
            >
              {isSubmitting && <span className="gnom-submit-spinner" aria-hidden="true" />}
              <span>
                {isSubmitting
                  ? 'Сохраняю…'
                  : editingTask
                    ? 'Сохранить изменения'
                    : 'Создать задачу'}
              </span>
            </button>
          </div>
        </form>

        {relationPicker && (
          <div
            className="gnom-picker-backdrop"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) setRelationPicker(null);
            }}
          >
            <section className="gnom-picker-sheet" role="dialog" aria-modal="true" aria-labelledby="gnom-picker-title">
              <header className="gnom-picker-header">
                <div>
                  <small>{relationPicker === 'client' ? 'КЛИЕНТ ЗАДАЧИ' : 'ОБЪЕКТ ЗАДАЧИ'}</small>
                  <h2 id="gnom-picker-title">{relationPicker === 'client' ? 'Выбрать клиента' : 'Выбрать объект'}</h2>
                </div>
                <button type="button" onClick={() => setRelationPicker(null)} aria-label="Закрыть выбор">×</button>
              </header>

              <label className="gnom-picker-search">
                <span aria-hidden="true">⌕</span>
                <input
                  ref={relationSearchInputRef}
                  type="search"
                  value={relationSearch}
                  onChange={(event) => setRelationSearch(event.target.value)}
                  autoComplete="on"
                  autoCapitalize="words"
                  spellCheck="true"
                  enterKeyHint="search"
                  placeholder={relationPicker === 'client' ? 'Имя, компания или телефон' : 'Название, адрес или метро'}
                  aria-label={relationPicker === 'client' ? 'Поиск клиента' : 'Поиск объекта'}
                />
                {relationSearch && <button type="button" onClick={() => setRelationSearch('')} aria-label="Очистить поиск">×</button>}
              </label>

              {relationPicker === 'object' && selectedClient && (
                <button type="button" className={`gnom-picker-scope ${showAllObjects ? '' : 'active'}`} onClick={() => setShowAllObjects(value => !value)}>
                  <span>{showAllObjects ? 'Показаны все объекты' : `Только объекты: ${selectedClient.name}`}</span>
                  <strong>{showAllObjects ? 'Вернуть фильтр' : 'Показать все'}</strong>
                </button>
              )}

              <div className="gnom-picker-list">
                <button
                  type="button"
                  className={`gnom-picker-option clear ${(relationPicker === 'client' ? !selectedClient : !selectedObject) ? 'selected' : ''}`}
                  onClick={() => relationPicker === 'client' ? chooseClient(null) : chooseObject(null)}
                >
                  <span className="gnom-picker-option-icon">∅</span>
                  <span><strong>{relationPicker === 'client' ? 'Без клиента' : 'Без объекта'}</strong><small>Оставить задачу без привязки</small></span>
                  {(relationPicker === 'client' ? !selectedClient : !selectedObject) && <em>✓</em>}
                </button>

                {relationPicker === 'client' && visibleClients.map(client => {
                  const objectCount = objects.filter(object => String(object.client_id || '') === String(client.id)).length;
                  const selected = String(client.id) === String(formData.client_id || '');
                  return (
                    <button key={client.id} type="button" className={`gnom-picker-option ${selected ? 'selected' : ''}`} onClick={() => chooseClient(client)}>
                      <span className="gnom-picker-option-icon client">{String(client.name || '?').trim().charAt(0).toLocaleUpperCase('ru-RU')}</span>
                      <span>
                        <strong>{client.name || 'Без имени'}</strong>
                        <small>{[client.company, client.phone, `${objectCount} объект${objectCount === 1 ? '' : objectCount >= 2 && objectCount <= 4 ? 'а' : 'ов'}`].filter(Boolean).join(' · ')}</small>
                      </span>
                      {selected && <em>✓</em>}
                    </button>
                  );
                })}

                {relationPicker === 'object' && visibleObjects.map(object => {
                  const linkedClient = clients.find(client => String(client.id) === String(object.client_id || ''));
                  const selected = String(object.id) === String(formData.object_id || '');
                  return (
                    <button key={object.id} type="button" className={`gnom-picker-option ${selected ? 'selected' : ''}`} onClick={() => chooseObject(object)}>
                      <span className="gnom-picker-option-icon object">🏢</span>
                      <span>
                        <strong>{object.name || 'Без названия'}</strong>
                        <small>{[linkedClient?.name || object.client_name, object.address, object.metro && `м. ${object.metro}`].filter(Boolean).join(' · ') || 'Без адреса и клиента'}</small>
                      </span>
                      {selected && <em>✓</em>}
                    </button>
                  );
                })}

                {relationPicker === 'client' && visibleClients.length === 0 && (
                  <div className="gnom-picker-empty"><span>⌕</span><strong>Клиент не найден</strong><small>Попробуй имя, компанию или часть телефона</small></div>
                )}
                {relationPicker === 'object' && visibleObjects.length === 0 && (
                  <div className="gnom-picker-empty"><span>⌕</span><strong>Объект не найден</strong><small>{selectedClient && !showAllObjects ? 'У этого клиента нет подходящего объекта — покажи все.' : 'Попробуй название или часть адреса.'}</small></div>
                )}
              </div>
            </section>
          </div>
        )}

        {showPaymentCreate && editingTask && (
          <div
            className="gnom-payment-create-backdrop"
            onMouseDown={(event) => {
              if (
                event.target === event.currentTarget
                && paymentCreatingTaskId !== taskServerId(editingTask)
              ) {
                setShowPaymentCreate(false);
              }
            }}
          >
            <form
              className="gnom-payment-create-dialog"
              onSubmit={submitPaymentCreate}
            >
              <header>
                <div>
                  <small>СБП-оплата</small>
                  <h2>Сформировать QR</h2>
                </div>
                <button
                  type="button"
                  className="gnom-payment-create-close"
                  onClick={() => setShowPaymentCreate(false)}
                  disabled={
                    paymentCreatingTaskId === taskServerId(editingTask)
                  }
                  aria-label="Закрыть"
                >
                  ×
                </button>
              </header>

              <label>
                Назначение платежа
                <input
                  type="text"
                  value={paymentDraft.title}
                  onChange={(event) => setPaymentDraft((previous) => ({
                    ...previous,
                    title: event.target.value,
                  }))}
                  autoComplete="on"
                  autoCapitalize="sentences"
                  spellCheck="true"
                  maxLength={255}
                  required
                />
              </label>

              <div className="gnom-payment-create-amounts">
                <label>
                  Работа
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    inputMode="decimal"
                    value={paymentDraft.workAmount}
                    onChange={(event) => setPaymentDraft((previous) => ({
                      ...previous,
                      workAmount: event.target.value,
                    }))}
                    placeholder="0"
                  />
                  <small>пойдёт в доход</small>
                </label>

                <label>
                  Оборудование
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    inputMode="decimal"
                    value={paymentDraft.equipmentAmount}
                    onChange={(event) => setPaymentDraft((previous) => ({
                      ...previous,
                      equipmentAmount: event.target.value,
                    }))}
                    placeholder="0"
                  />
                  <small>в доход не попадёт</small>
                </label>
              </div>

              <div className="gnom-payment-create-total">
                <span>QR на оплату</span>
                <strong>
                  {paymentDraftTotal.toLocaleString('ru-RU', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })} ₽
                </strong>
              </div>

              <button
                type="submit"
                className="gnom-payment-create-submit"
                disabled={
                  !paymentDraft.title.trim()
                  || paymentDraftTotal <= 0
                  || paymentCreatingTaskId === taskServerId(editingTask)
                }
              >
                {paymentCreatingTaskId === taskServerId(editingTask)
                  ? 'Создаю QR…'
                  : 'Создать QR'}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default TaskForm;
